import { useState, useMemo, useEffect } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxes,
  faPlus,
  faStore,
  faBox,
  faArrowUp,
  faArrowDown,
  faFilter,
  faChevronRight,
  faTimes,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import AddStock from "./shop/AddStock";
import EditStock from "./shop/EditStock";
import ProductStockDetails from "./shop/ProductStockDetails";
import SearchBar from "../../components/ui/SearchBar";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import { useAuth, useShops } from "../../hooks";
import { useMyShopsStocks } from "../../hooks/useStock";
import { useProducts } from "../../hooks/useProducts";
import { toastError } from "../../utils/toast";

const getRemaining = (stock) => Number(stock.summary?.remainingItems ?? 0);

const computeStockTotals = (stock) => ({
  in: Number(stock.summary?.totalItems ?? 0),
  out: Number(stock.summary?.itemsSold ?? 0),
  remaining: Number(stock.summary?.remainingItems ?? 0),
});

const ManageStockO = () => {
  const { hasPermission } = useAuth();
  const canManageStock = hasPermission(["manage_stock", "manage_shop_modules"]);

  const {
    stocks,
    pagination,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    shopFilter,
    setShopFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refresh,
    createStock,
    updateStock,
  } = useMyShopsStocks({ per_page: 10 });

  const { shops } = useShops({ per_page: 1000 });
  const { products } = useProducts({ per_page: 1000 });

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProductDetailsOpen, setIsProductDetailsOpen] = useState(false);
  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [selectedShopForDetails, setSelectedShopForDetails] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockStatusFilter, setStockStatusFilter] = useState("all");

  const [isAdvFilterOpen, setIsAdvFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    startDate: "",
    endDate: "",
    shopFilter: "",
    stockStatus: "all",
  });

  const [expandedProductId, setExpandedProductId] = useState(null);

  // Local state for the search input to prevent UI reload on every keystroke
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounce effect: Waits 500ms after user stops typing before triggering the actual API search
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm, setSearchTerm, setCurrentPage]);

  const safeStocks = useMemo(
    () => (Array.isArray(stocks) ? stocks : []),
    [stocks],
  );
  const safeShops = useMemo(() => (Array.isArray(shops) ? shops : []), [shops]);
  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products],
  );

  const enrichedStocks = useMemo(() => {
    const activeOnly = safeStocks.filter(
      (stock) => stock.status === "active" || stock.status === "ACTIVE",
    );

    if (!activeOnly.length || !safeProducts.length) return activeOnly;

    return activeOnly.map((stock) => {
      const product = safeProducts.find((p) => p.productId === stock.productId);
      return {
        ...stock,
        product: product
          ? {
              ...product,
              product_type:
                product.product_category?.product_type?.name || null,
              category_name: product.product_category?.category_name || null,
              product_category: product.product_category,
            }
          : null,
      };
    });
  }, [safeStocks, safeProducts]);

  const productGroups = useMemo(() => {
    const map = new Map();

    enrichedStocks.forEach((stock) => {
      const productId = stock.productId;
      const product = stock.product;

      if (!map.has(productId)) {
        map.set(productId, {
          productId,
          product,
          productName: product?.product_name || "Unknown product",
          categoryName: product?.category_name || null,
          stocks: [],
          shopIds: new Set(),
        });
      }

      const group = map.get(productId);
      group.stocks.push(stock);
      if (stock.shopId) group.shopIds.add(stock.shopId);
    });

    return Array.from(map.values()).map((group) => {
      let totalDozen = 0;
      let totalItems = 0;
      let totalPurchase = 0;
      let totalSelling = 0;
      let remainingItems = 0;
      let soldItems = 0;

      group.stocks.forEach((s) => {
        totalDozen += Number(s.dozen_amount ?? 0);
        totalItems += Number(s.total_items ?? 0);
        totalPurchase += Number(s.purchase_price_total ?? 0);
        totalSelling += Number(s.selling_price_total ?? 0);
        remainingItems += Number(s.summary?.remainingItems ?? 0);
        soldItems += Number(s.summary?.itemsSold ?? 0);
      });

      const expectedProfit = totalSelling - totalPurchase;
      const profitMargin =
        totalPurchase > 0 ? (expectedProfit / totalPurchase) * 100 : 0;

      return {
        ...group,
        shopIds: Array.from(group.shopIds),
        shopCount: group.shopIds.size,
        stockCount: group.stocks.length,
        totalDozen,
        totalItems,
        totalPurchase,
        totalSelling,
        expectedProfit,
        profitMargin,
        remainingItems,
        soldItems,
      };
    });
  }, [enrichedStocks]);

  const buildShopRows = (group) => {
    if (!group?.stocks?.length) return [];
    const map = new Map();

    group.stocks.forEach((stock) => {
      const shopId = stock.shopId;
      if (!map.has(shopId)) {
        map.set(shopId, {
          shopId,
          shopName:
            safeShops.find((s) => s.shopId === shopId)?.shop_name ||
            stock.shopName ||
            "Unknown shop",
          stocks: [],
          stockIn: 0,
          stockOut: 0,
          remaining: 0,
        });
      }
      const row = map.get(shopId);
      row.stocks.push(stock);

      const totals = computeStockTotals(stock);
      row.stockIn += totals.in;
      row.stockOut += totals.out;
      row.remaining += totals.remaining;
    });

    return Array.from(map.values());
  };

  const filteredGroups = useMemo(() => {
    if (stockStatusFilter === "all") return productGroups;

    return productGroups.filter((group) => {
      if (stockStatusFilter === "in") return group.remainingItems > 0;
      if (stockStatusFilter === "out") return group.remainingItems === 0;
      return true;
    });
  }, [productGroups, stockStatusFilter]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0)) + " TSH";

  const summaryStats = useMemo(() => {
    let totalStockRecords = 0;
    let totalItems = 0;
    let stockIn = 0;
    let stockOut = 0;

    enrichedStocks.forEach((stock) => {
      const remaining = getRemaining(stock);

      totalStockRecords += 1;
      totalItems += Number(stock.total_items ?? 0);

      if (remaining > 0) stockIn += 1;
      else stockOut += 1;
    });

    return {
      total: totalStockRecords,
      totalItems,
      activeStocks: stockIn,
      inactiveStocks: stockOut,
    };
  }, [enrichedStocks]);

  const totalItems = pagination?.total ?? filteredGroups.length;
  const totalPages = pagination?.last_page ?? 1;
  const startIndex = (currentPage - 1) * pageSize;

  const handleAddStock = async (stockData) => {
    try {
      await createStock(stockData);
      setIsAddModalOpen(false);
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to add stock");
    }
  };

  const handleUpdateStock = async (stockData) => {
    try {
      const id = stockData?.stockId || stockData?.id;
      if (!id) return;
      await updateStock(id, stockData);
      setIsEditModalOpen(false);
      setSelectedStock(null);
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update stock");
    }
  };

  const toggleExpanded = (productId) => {
    setExpandedProductId((prev) => (prev === productId ? null : productId));
  };

  const openProductDetailsForShop = (group, shop) => {
    setSelectedProductGroup(group);
    setSelectedShopForDetails(shop);
    setIsProductDetailsOpen(true);
  };

  const openEditForStock = (stock) => {
    setSelectedStock(stock);
    setIsProductDetailsOpen(false);
    setIsEditModalOpen(true);
  };

  const openAdvFilter = () => {
    setTempFilters({
      startDate: startDate || "",
      endDate: endDate || "",
      shopFilter: shopFilter || "",
      stockStatus: stockStatusFilter || "all",
    });
    setIsAdvFilterOpen(true);
  };

  const applyAdvFilters = () => {
    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setShopFilter(
      tempFilters.shopFilter === "all" ? "" : tempFilters.shopFilter,
    );
    setStockStatusFilter(tempFilters.stockStatus);
    setIsFilterApplied(
      !!(
        tempFilters.startDate ||
        tempFilters.endDate ||
        tempFilters.shopFilter !== "all" ||
        tempFilters.stockStatus !== "all"
      ),
    );
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const clearAdvFilters = () => {
    setTempFilters({
      startDate: "",
      endDate: "",
      shopFilter: "",
      stockStatus: "all",
    });
    setStartDate("");
    setEndDate("");
    setShopFilter("");
    setStockStatusFilter("all");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  if (loading && safeStocks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <FontAwesomeIcon
          icon={faBoxes}
          className="h-10 w-10 text-blue-600 animate-pulse"
        />
        <p className="mt-4 text-gray-600 text-sm">Loading stock records...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <p className="text-red-600 text-sm">
          Failed to load stock records. Please try again.
        </p>
        <Button
          onClick={() => refresh()}
          className="mt-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-8xl mx-auto px-3 sm:px-4 lg:px-0">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faBoxes}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Manage stocks
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Manage inventory and stock across all your shops
              </p>
            </div>
          </div>
          {canManageStock && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add stock
            </Button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBoxes}
                className="text-blue-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Total Stock</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {summaryStats.total}
          </p>
        </Card>
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBox}
                className="text-green-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Total Items</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {summaryStats.totalItems.toLocaleString()}
          </p>
        </Card>
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowUp}
                className="text-green-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Stock In</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
            {summaryStats.activeStocks}
          </p>
        </Card>
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowDown}
                className="text-red-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Stock Out</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600">
            {summaryStats.inactiveStocks}
          </p>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={localSearchTerm}
          onChange={(value) => setLocalSearchTerm(value)}
          placeholder="Search by product, category, type, or shop..."
          className="flex-1"
        />
        <Button
          onClick={openAdvFilter}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6"
        >
          <FontAwesomeIcon icon={faFilter} />
          Advanced Filter
          {isFilterApplied && (
            <span className="ml-1 w-2 h-2 rounded-full bg-orange-400"></span>
          )}
        </Button>
      </div>

      <TableWrapper
        title="stock records"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                #
              </th>
              <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                Product
              </th>
              <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden lg:table-cell">
                Shops
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                Dozen
              </th>
              <th className="text-right text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                Purchase
              </th>
              <th className="text-right text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                Selling
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                Expected profit
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredGroups.length > 0 ? (
              filteredGroups.map((group, i) => {
                const globalIndex = startIndex + i + 1;
                const shopCount = group.shopCount;
                const isExpanded = expandedProductId === group.productId;
                const shopRows = isExpanded ? buildShopRows(group) : [];

                return (
                  <>
                    <tr
                      key={group.productId}
                      onClick={() => toggleExpanded(group.productId)}
                      className={`group cursor-pointer transition-colors ${
                        isExpanded ? "bg-blue-50/70" : "hover:bg-blue-50/60"
                      }`}
                      title="Click to view shops carrying this product"
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 font-medium">
                        {globalIndex}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-blue-800">
                        <div>
                          <p className="font-medium">{group.productName}</p>
                          <p className="text-sm text-orange-600 font-semibold">
                            {group.categoryName || "N/A"}
                          </p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {shopCount} {shopCount === 1 ? "shop" : "shops"} ·{" "}
                            {group.stockCount}{" "}
                            {group.stockCount === 1 ? "stock" : "stocks"}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden lg:table-cell">
                        {shopCount === 1
                          ? safeShops.find((s) => s.shopId === group.shopIds[0])
                              ?.shop_name || "-"
                          : `${shopCount} shops`}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-semibold text-gray-900 hidden md:table-cell">
                        {group.totalDozen}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm text-gray-600 hidden sm:table-cell">
                        {formatCurrency(group.totalPurchase)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm text-blue-800 font-medium hidden sm:table-cell">
                        {formatCurrency(group.totalSelling)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-center hidden md:table-cell">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            group.expectedProfit > 0
                              ? "bg-green-600 text-white border-green-200"
                              : group.expectedProfit < 0
                                ? "bg-red-600 text-white border-red-200"
                                : "bg-gray-600 text-white border-gray-200"
                          }`}
                        >
                          {formatCurrency(group.expectedProfit)}
                          <span className="ml-1 text-[8px] opacity-80">
                            ({group.profitMargin.toFixed(1)}%)
                          </span>
                        </Badge>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className={`h-3.5 w-3.5 transition-all ${
                            isExpanded
                              ? "text-blue-700 rotate-90"
                              : "text-gray-400 group-hover:text-blue-700 group-hover:translate-x-0.5"
                          }`}
                        />
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr key={`${group.productId}-expansion`}>
                        <td
                          colSpan={8}
                          className="bg-blue-50/40 px-3 sm:px-6 py-3 border-t border-b border-blue-100"
                        >
                          <div className="rounded-lg border border-blue-100 bg-white overflow-hidden shadow-sm">
                            <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-white border-b border-blue-100 flex items-center justify-between">
                              <p className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-2">
                                <FontAwesomeIcon
                                  icon={faStore}
                                  className="h-3 w-3 text-blue-600"
                                />
                                Shops carrying this product
                              </p>
                              <span className="text-sm text-gray-500">
                                {shopRows.length}{" "}
                                {shopRows.length === 1 ? "shop" : "shops"}
                              </span>
                            </div>

                            <div className="divide-y divide-gray-50">
                              {shopRows.map((row) => (
                                <button
                                  key={row.shopId}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openProductDetailsForShop(group, {
                                      shopId: row.shopId,
                                      shopName: row.shopName,
                                      shop_name: row.shopName,
                                    });
                                  }}
                                  className="w-full text-left px-3 sm:px-4 py-2.5 hover:bg-blue-50/60 transition-colors group/shop"
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                        <FontAwesomeIcon
                                          icon={faStore}
                                          className="h-3 w-3 text-blue-600"
                                        />
                                        {row.shopName}
                                      </p>
                                      <p className="text-sm text-gray-500 mt-0.5">
                                        {row.stocks.length}{" "}
                                        {row.stocks.length === 1
                                          ? "stock"
                                          : "stocks"}{" "}
                                        · IN{" "}
                                        <span className="text-green-600 font-semibold">
                                          {row.stockIn}
                                        </span>{" "}
                                        · OUT{" "}
                                        <span className="text-red-600 font-semibold">
                                          {row.stockOut}
                                        </span>{" "}
                                        · Rem{" "}
                                        <span className="text-blue-600 font-semibold">
                                          {row.remaining}
                                        </span>
                                      </p>
                                    </div>
                                    <FontAwesomeIcon
                                      icon={faChevronRight}
                                      className="h-3 w-3 text-gray-400 group-hover/shop:text-blue-700 group-hover/shop:translate-x-0.5 transition-all"
                                    />
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-6 sm:py-8">
                  <EmptyState
                    title="No stock records found"
                    message={
                      searchTerm ||
                      shopFilter ||
                      stockStatusFilter !== "all" ||
                      isFilterApplied
                        ? "Try adjusting your search or filters"
                        : canManageStock
                          ? "Add your first stock record to get started"
                          : "No stock records yet"
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {canManageStock && isAddModalOpen && (
        <AddStock
          onAdd={handleAddStock}
          onClose={() => setIsAddModalOpen(false)}
          shops={safeShops}
          products={safeProducts}
        />
      )}

      {canManageStock && isEditModalOpen && selectedStock && (
        <EditStock
          stock={selectedStock}
          onUpdate={handleUpdateStock}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedStock(null);
          }}
          shops={safeShops}
          products={safeProducts}
        />
      )}

      {isProductDetailsOpen && selectedProductGroup && (
        <ProductStockDetails
          productGroup={selectedProductGroup}
          shop={selectedShopForDetails}
          shops={safeShops}
          onClose={() => {
            setIsProductDetailsOpen(false);
            setSelectedProductGroup(null);
            setSelectedShopForDetails(null);
          }}
          canManageStock={canManageStock}
          onEditStock={(stock) => openEditForStock(stock)}
        />
      )}

      {isAdvFilterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Advanced Filter
              </h3>
              <button
                onClick={() => setIsAdvFilterOpen(false)}
                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-blue-600 h-4 w-4"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Filter Records
                  </h4>
                  <p className="text-xs text-gray-600">
                    Select criteria to narrow down results
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Filter by Date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      From
                    </label>
                    <input
                      type="date"
                      value={tempFilters.startDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      To
                    </label>
                    <input
                      type="date"
                      value={tempFilters.endDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faBoxes}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Stock Status
                </label>
                <Select
                  value={tempFilters.stockStatus}
                  onValueChange={(val) =>
                    setTempFilters({ ...tempFilters, stockStatus: val })
                  }
                >
                  <SelectTrigger className="w-full h-10 border border-gray-300 rounded-lg bg-gray-50/50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <SelectValue placeholder="All Stock" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stock</SelectItem>
                    <SelectItem value="in">
                      Stock In (Remaining &gt; 0)
                    </SelectItem>
                    <SelectItem value="out">
                      Stock Out (Remaining = 0)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faStore}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Shop
                </label>
                <Select
                  value={tempFilters.shopFilter || "all"}
                  onValueChange={(val) =>
                    setTempFilters({ ...tempFilters, shopFilter: val })
                  }
                >
                  <SelectTrigger className="w-full h-10 border border-gray-300 rounded-lg bg-gray-50/50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <SelectValue placeholder="All Shops" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Shops</SelectItem>
                    {safeShops.map((shop) => (
                      <SelectItem key={shop.shopId} value={shop.shopId}>
                        {shop.shop_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={clearAdvFilters}
                className="border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none px-6"
              >
                Clear All
              </Button>
              <Button
                onClick={applyAdvFilters}
                className="bg-blue-800 hover:bg-blue-900 text-white px-6 shadow-sm"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStockO;
